import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ImagePlus, Trash2, Plus, Film, Image, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GalleryPage() {
  const { token, organization } = useAuth();
  const [segments, setSegments] = useState([]);
  const [newSegmentName, setNewSegmentName] = useState('');
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);

  useEffect(() => {
    if (organization) fetchGallery();
  }, [organization]);

  const fetchGallery = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gallery/${organization.org_id}`);
      if (res.ok) setSegments(await res.json());
    } catch (e) { console.error(e); }
  };

  const createSegment = async () => {
    if (!newSegmentName.trim()) return toast.error('Enter a segment name');
    try {
      const res = await fetch(`${API_URL}/api/gallery/segments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: newSegmentName.trim() })
      });
      if (res.ok) {
        setNewSegmentName('');
        fetchGallery();
        toast.success('Segment created');
      }
    } catch { toast.error('Failed to create segment'); }
  };

  const deleteSegment = async (segmentId) => {
    if (!window.confirm('Delete this segment and all its media?')) return;
    try {
      await fetch(`${API_URL}/api/gallery/segments/${segmentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchGallery();
      toast.success('Segment deleted');
    } catch { toast.error('Failed'); }
  };

  const handleUploadClick = (segmentId) => {
    setUploadTarget(segmentId);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const files = e.target.files;
    if (!files?.length || !uploadTarget) return;
    
    setUploading(uploadTarget);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API_URL}/api/gallery/segments/${uploadTarget}/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          toast.error(err.detail || `Failed to upload ${file.name}`);
        }
      }
      fetchGallery();
      toast.success('Upload complete');
    } catch { toast.error('Upload failed'); }
    finally {
      setUploading(null);
      setUploadTarget(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const isVideo = (ct) => ct?.startsWith('video/');

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="gallery-page">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-black uppercase tracking-tight">Photo Gallery</h1>
          <p className="text-gray-600 mt-2">Upload photos and videos from the competition</p>
        </div>

        <div className="flex gap-3">
          <Input
            placeholder="Segment name (e.g. Opening Ceremony)"
            value={newSegmentName}
            onChange={(e) => setNewSegmentName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && createSegment()}
            className="bg-white border-gray-200 text-black max-w-sm"
            data-testid="new-segment-name"
          />
          <Button onClick={createSegment} className="bg-emerald-500 hover:bg-emerald-600 text-black" data-testid="create-segment-btn">
            <Plus size={16} className="mr-1" /> New Segment
          </Button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />

        {segments.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <ImagePlus className="mx-auto text-gray-600 mb-4" size={48} />
            <h3 className="text-black text-lg font-medium mb-2">No gallery segments yet</h3>
            <p className="text-gray-500">Create a segment above to start uploading photos and videos</p>
          </div>
        ) : (
          <div className="space-y-6">
            {segments.map((seg) => (
              <Card key={seg.segment_id} className="bg-white border-gray-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-black font-bold text-lg">{seg.name}</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-xs">{(seg.media || []).length}/5 files</span>
                      {(seg.media || []).length < 5 && (
                        <Button
                          size="sm"
                          onClick={() => handleUploadClick(seg.segment_id)}
                          disabled={uploading === seg.segment_id}
                          className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400"
                          data-testid={`upload-${seg.segment_id}`}
                        >
                          {uploading === seg.segment_id ? <Loader2 size={14} className="animate-spin mr-1" /> : <ImagePlus size={14} className="mr-1" />}
                          Upload
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteSegment(seg.segment_id)} className="text-gray-600 hover:text-red-500">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                  {(seg.media || []).length === 0 ? (
                    <p className="text-gray-600 text-sm text-center py-8">No media uploaded yet. Click Upload to add photos or videos.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                      {(seg.media || []).map((m) => (
                        <div key={m.media_id} className="relative group rounded-lg overflow-hidden bg-black aspect-square">
                          {isVideo(m.content_type) ? (
                            <video
                              src={`${API_URL}/api/gallery/media/${m.storage_path}`}
                              className="w-full h-full object-cover"
                              controls
                              preload="metadata"
                            />
                          ) : (
                            <img
                              src={`${API_URL}/api/gallery/media/${m.storage_path}`}
                              alt={m.original_filename}
                              className="w-full h-full object-cover"
                            />
                          )}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                            <div className="flex items-center gap-1">
                              {isVideo(m.content_type) ? <Film size={10} className="text-blue-400" /> : <Image size={10} className="text-emerald-400" />}
                              <span className="text-black text-[9px] truncate">{m.original_filename}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
